import { useState } from "react";
import { useProfile } from "@/context/ProfileContext";
import api, { formatApiErrorDetail, isUpgradeRequired } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronDown, Users, Plus, Trash2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

const RELATIONS = [
  { v: "self", l: "Myself" },
  { v: "partner", l: "Partner" },
  { v: "parent", l: "Parent" },
  { v: "child", l: "Child" },
  { v: "other", l: "Other" },
];

function Initials({ name, color }) {
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold shrink-0"
      style={{ background: color, color: "white" }}
    >
      {initials}
    </span>
  );
}

export default function ProfileSwitcher() {
  const { profiles, active, setActiveId, refresh } = useProfile();
  const navigate = useNavigate();
  const [manageOpen, setManageOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addRelation, setAddRelation] = useState("partner");
  const [busy, setBusy] = useState(false);

  if (!active) return null;

  const addProfile = async () => {
    if (!addName.trim()) return toast.error("Please enter a name.");
    setBusy(true);
    try {
      const { data } = await api.post("/profiles", { name: addName, relation: addRelation });
      await refresh();
      setActiveId(data.id);
      setAddName("");
      setAddRelation("partner");
      toast.success(`Added ${data.name}`);
    } catch (e) {
      if (isUpgradeRequired(e)) {
        setManageOpen(false);
        toast.info("Family profiles are a Pro feature. Redirecting…");
        setTimeout(() => navigate("/pricing"), 800);
      } else {
        toast.error("Could not add profile.");
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, isDefault) => {
    if (isDefault) return toast.error("The default profile cannot be deleted.");
    if (!window.confirm("Delete this profile and all its data?")) return;
    setBusy(true);
    try {
      await api.delete(`/profiles/${id}`);
      if (id === active.id) {
        const def = profiles.find((p) => p.is_default);
        if (def) setActiveId(def.id);
      }
      await refresh();
      toast.success("Profile removed.");
    } catch (_e) {
      toast.error("Could not delete profile.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="profile-switcher-trigger"
            className="inline-flex items-center gap-2 rounded-full px-2.5 py-1.5 border transition-colors hover:bg-[#F4EFE6]"
            style={{ borderColor: "#E5DEC9", background: "#FFFFFF" }}
          >
            <Initials name={active.name} color={active.color} />
            <span className="text-sm font-semibold hidden sm:inline" style={{ color: "#1C2826" }}>
              {active.name}
            </span>
            <ChevronDown size={14} style={{ color: "#5C6B66" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-64 rounded-2xl"
          style={{ background: "#FFFFFF", border: "1px solid #E5DEC9" }}
        >
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest" style={{ color: "#5C6B66" }}>
            Switch profile
          </DropdownMenuLabel>
          {profiles.map((p) => (
            <DropdownMenuItem
              key={p.id}
              data-testid={`profile-option-${p.id}`}
              onClick={() => setActiveId(p.id)}
              className="rounded-xl cursor-pointer py-2"
            >
              <div className="flex items-center gap-2 flex-1">
                <Initials name={p.name} color={p.color} />
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                    {p.name}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest" style={{ color: "#5C6B66" }}>
                    {p.relation}
                  </p>
                </div>
                {p.id === active.id && <Check size={14} style={{ color: "#2D5A4C" }} />}
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="profile-manage-btn"
            onClick={() => setManageOpen(true)}
            className="rounded-xl cursor-pointer"
          >
            <Users size={14} className="mr-2" /> Manage profiles
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="profile-manage-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
              <Users size={20} /> Family profiles
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2 max-h-64 overflow-auto">
            {profiles.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 p-3 rounded-xl border"
                style={{ borderColor: "#E5DEC9", background: p.id === active.id ? "#E8F0EC" : "#FAF7F2" }}
              >
                <Initials name={p.name} color={p.color} />
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                    {p.name}
                    {p.is_default && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: "#2D5A4C" }}>
                        default
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest" style={{ color: "#5C6B66" }}>
                    {p.relation}
                  </p>
                </div>
                {!p.is_default && (
                  <button
                    data-testid={`profile-delete-${p.id}`}
                    onClick={() => remove(p.id, p.is_default)}
                    className="p-2 rounded-full hover:bg-[#FDF0EA]"
                  >
                    <Trash2 size={14} style={{ color: "#BF5832" }} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div
            className="mt-2 p-4 rounded-2xl border space-y-3"
            style={{ borderColor: "#E5DEC9", background: "#FFFFFF" }}
          >
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
              Add a new profile
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name</Label>
                <Input
                  data-testid="profile-add-name-input"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  className="mt-1.5 h-11 rounded-xl"
                  placeholder="e.g. Mom, Alex, Baby"
                />
              </div>
              <div className="col-span-2">
                <Label>Relation</Label>
                <Select value={addRelation} onValueChange={setAddRelation}>
                  <SelectTrigger data-testid="profile-add-relation-trigger" className="mt-1.5 h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONS.map((r) => (
                      <SelectItem key={r.v} value={r.v}>
                        {r.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              data-testid="profile-add-submit-btn"
              onClick={addProfile}
              disabled={busy}
              className="btn-brand rounded-full h-11 w-full"
            >
              <Plus size={14} className="mr-1" /> Add profile
            </Button>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setManageOpen(false)}
              className="rounded-full"
              data-testid="profile-manage-close-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
